/**
 * PaymentSummaryDialog - Step 2 of POS Two-Step Checkout
 * 
 * Compact design optimized for fast billing without scrollbars.
 * The actual sale creation (POST /api/sales) only happens on "Confirm Payment & Complete Sale".
 * 
 * NO stock deduction, ledger entries, or cash register changes occur in this dialog.
 * All financial operations are handled by the existing backend endpoint.
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, AlertCircle, ArrowLeft, Check, User, Package } from "lucide-react";

// Round to 2 decimal places
function r2(v) {
  return Math.round(Number(v || 0) * 100) / 100;
}

export default function PaymentSummaryDialog({
  open,
  onOpenChange,
  // Cart data from Step 1
  customer,
  customerName,
  lines,
  subtotal,
  billDiscountPct,
  billDiscountAmount,
  total,
  roundOffAmount,
  // Store credit info
  availableStoreCredit = 0,
  // Outstanding dues
  outstandingDues = 0,
  // Callbacks
  onBack, // Return to cart (Step 1)
  onConfirm, // Confirm payment & complete sale
  saving = false,
}) {
  // Payment state
  const [cashAmount, setCashAmount] = useState(0);
  const [upiAmount, setUpiAmount] = useState(0);
  const [cardAmount, setCardAmount] = useState(0);
  const [creditAmount, setCreditAmount] = useState(0);
  const [storeCreditAmount, setStoreCreditAmount] = useState(0);
  const [storeExcessAsCredit, setStoreExcessAsCredit] = useState(false);
  
  // Dues collection
  const [duesCollectionAmount, setDuesCollectionAmount] = useState(0);
  const [duesCollectionMode, setDuesCollectionMode] = useState("cash");
  
  // Cash received for change calculation
  const [cashReceived, setCashReceived] = useState(0);
  
  // Double-click prevention
  const confirmingRef = useRef(false);
  
  // Reset payment state when dialog opens with new data
  useEffect(() => {
    if (open) {
      // Reset double-click prevention flag when dialog opens
      confirmingRef.current = false;
      
      // Auto-fill: Store credit first (up to total), then cash for remainder
      const storeCreditToUse = availableStoreCredit > 0 
        ? Math.min(availableStoreCredit, total) 
        : 0;
      
      setStoreCreditAmount(r2(storeCreditToUse));
      const remaining = r2(total - storeCreditToUse);
      setCashAmount(Math.max(0, remaining));
      // Cash Received must be blank by default - cashier enters actual amount received
      setCashReceived(0);
      setUpiAmount(0);
      setCardAmount(0);
      setCreditAmount(0);
      setStoreExcessAsCredit(false);
      setDuesCollectionAmount(0);
      setDuesCollectionMode("cash");
      confirmingRef.current = false;
    }
  }, [open, total, availableStoreCredit]);
  
  // Calculate totals
  // paymentsTotal = all allocations including Credit/Due (for validation)
  const paymentsTotal = useMemo(() => 
    r2(cashAmount + upiAmount + cardAmount + creditAmount + storeCreditAmount),
    [cashAmount, upiAmount, cardAmount, creditAmount, storeCreditAmount]
  );
  
  // paidAmount = actual amount paid NOW (excludes Credit/Due which is deferred)
  // PAID = CASH + UPI + CARD + STORE CREDIT (not Credit/Due)
  const paidAmount = useMemo(() =>
    r2(cashAmount + upiAmount + cardAmount + storeCreditAmount),
    [cashAmount, upiAmount, cardAmount, storeCreditAmount]
  );
  
  const paidWithoutCash = useMemo(() =>
    r2(upiAmount + cardAmount + creditAmount + storeCreditAmount),
    [upiAmount, cardAmount, creditAmount, storeCreditAmount]
  );
  
  // Remaining after non-cash payments
  const remainingForCash = useMemo(() =>
    r2(Math.max(0, total - paidWithoutCash)),
    [total, paidWithoutCash]
  );
  
  // Cash change calculation (against remaining after other payments)
  const cashChange = useMemo(() => {
    if (cashReceived <= 0) return 0;
    const cashNeeded = Math.min(cashAmount, remainingForCash);
    return r2(Math.max(0, cashReceived - cashNeeded));
  }, [cashReceived, cashAmount, remainingForCash]);
  
  // Excess payment (payments exceed total)
  const excessAmount = useMemo(() => {
    const diff = paymentsTotal - total;
    return diff > 0.01 ? r2(diff) : 0;
  }, [paymentsTotal, total]);
  
  // Shortfall (payments less than total)
  const shortfall = useMemo(() => {
    const diff = total - paymentsTotal;
    return diff > 0.01 ? r2(diff) : 0;
  }, [total, paymentsTotal]);
  
  // Cash shortfall - when cash received is less than cash due
  // Only applies when cash payment is part of the transaction
  const cashShortfall = useMemo(() => {
    // No cash payment = no cash validation needed
    if (cashAmount <= 0) return 0;
    // Cash received not entered yet = shortfall equals full cash due
    if (cashReceived <= 0) return r2(cashAmount);
    // Cash received less than cash due = shortfall
    const diff = cashAmount - cashReceived;
    return diff > 0.01 ? r2(diff) : 0;
  }, [cashAmount, cashReceived]);
  
  // Combined validation: is payment valid for completion?
  const canComplete = useMemo(() => {
    // Payment allocation shortfall
    if (shortfall > 0.01) return false;
    // Cash received shortfall (only when cash is used)
    if (cashAmount > 0 && cashShortfall > 0.01) return false;
    // Excess without customer approval
    if (excessAmount > 0.01 && !storeExcessAsCredit) return false;
    // No payments
    if (paymentsTotal === 0) return false;
    return true;
  }, [shortfall, cashAmount, cashShortfall, excessAmount, storeExcessAsCredit, paymentsTotal]);
  
  // Total items count
  const totalItems = useMemo(() => 
    lines.reduce((sum, l) => sum + (l.quantity || 1), 0),
    [lines]
  );
  
  // Should show store credit section
  const shouldShowStoreCredit = customer && availableStoreCredit > 0;
  
  // Validate and confirm payment
  const handleConfirm = () => {
    if (confirmingRef.current) return;
    
    if (storeCreditAmount > availableStoreCredit + 0.01) return;
    if (shortfall > 0.01) return;
    
    // Validate cash received against cash due
    if (cashAmount > 0 && cashShortfall > 0.01) return;
    
    if (excessAmount > 0.01) {
      if (!customer) return;
      if (!storeExcessAsCredit) return;
    }
    
    const payments = [];
    if (cashAmount > 0) payments.push({ mode: "cash", amount: r2(cashAmount) });
    if (upiAmount > 0) payments.push({ mode: "upi", amount: r2(upiAmount) });
    if (cardAmount > 0) payments.push({ mode: "card", amount: r2(cardAmount) });
    if (creditAmount > 0) payments.push({ mode: "credit", amount: r2(creditAmount) });
    if (storeCreditAmount > 0) payments.push({ mode: "store_credit", amount: r2(storeCreditAmount) });
    
    if (payments.length === 0) return;
    
    confirmingRef.current = true;
    
    onConfirm({
      payments,
      excessAsCredit: storeExcessAsCredit && excessAmount > 0.01 ? r2(excessAmount) : 0,
      duesCollection: duesCollectionAmount > 0 ? {
        amount: r2(duesCollectionAmount),
        mode: duesCollectionMode,
      } : null,
    });
  };
  
  // Auto-adjust cash amount when other payments change
  // Do NOT overwrite cashReceived - that's the actual amount the cashier entered
  useEffect(() => {
    if (!open) return;
    const remaining = r2(total - upiAmount - cardAmount - creditAmount - storeCreditAmount);
    if (remaining >= 0) {
      setCashAmount(remaining);
      // Do not auto-populate cashReceived - cashier must manually enter actual cash tendered
    }
  }, [upiAmount, cardAmount, creditAmount, storeCreditAmount, total, open]);
  
  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-[480px] w-[95vw] p-4 gap-0"
        data-testid="payment-summary-dialog"
        hideCloseButton={true}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Header - Compact */}
        <DialogHeader className="pb-2 space-y-0">
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            Payment Summary
          </DialogTitle>
          <DialogDescription className="sr-only">
            Review payment details and confirm to complete the sale
          </DialogDescription>
        </DialogHeader>
        
        {/* Customer & Items - Inline compact row */}
        <div className="flex items-center gap-3 py-2 text-xs border-b border-slate-100">
          <div className="flex items-center gap-1.5 text-slate-600">
            <User className="w-3.5 h-3.5" />
            <span className="font-medium truncate max-w-[140px]">
              {customer?.name || customerName || "Walk-in"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <Package className="w-3.5 h-3.5" />
            <span>{lines.length} items, {totalItems} qty</span>
          </div>
        </div>
        
        {/* TOTAL TO PAY - Prominent */}
        <div className="flex items-center justify-between py-3 border-b border-slate-200">
          <div className="text-xs">
            <div className="text-slate-500">Subtotal: ₹{subtotal.toFixed(2)}</div>
            {billDiscountPct > 0 && (
              <div className="text-red-500">Discount: −₹{billDiscountAmount.toFixed(2)}</div>
            )}
            {roundOffAmount !== 0 && (
              <div className="text-slate-500">Round: {roundOffAmount > 0 ? "+" : ""}₹{roundOffAmount.toFixed(2)}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Total to Pay</div>
            <div className="font-mono text-2xl font-bold text-slate-900" data-testid="payment-total-amount">
              ₹{total.toFixed(2)}
            </div>
          </div>
        </div>
        
        {/* Payment Methods - 2x2 Grid */}
        <div className="py-3 border-b border-slate-100">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            Payment Method
          </Label>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-2">
            {/* Cash */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 w-12 shrink-0">Cash</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={cashAmount || ""}
                onChange={(e) => setCashAmount(parseFloat(e.target.value || "0"))}
                className="font-mono h-8 text-sm px-2 text-right"
                data-testid="payment-cash"
              />
            </div>
            {/* UPI */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 w-12 shrink-0">UPI</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={upiAmount || ""}
                onChange={(e) => setUpiAmount(parseFloat(e.target.value || "0"))}
                className="font-mono h-8 text-sm px-2 text-right"
                data-testid="payment-upi"
              />
            </div>
            {/* Card */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 w-12 shrink-0">Card</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={cardAmount || ""}
                onChange={(e) => setCardAmount(parseFloat(e.target.value || "0"))}
                className="font-mono h-8 text-sm px-2 text-right"
                data-testid="payment-card"
              />
            </div>
            {/* Credit/Due */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 w-12 shrink-0">Credit</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={creditAmount || ""}
                onChange={(e) => setCreditAmount(parseFloat(e.target.value || "0"))}
                className="font-mono h-8 text-sm px-2 text-right"
                data-testid="payment-credit"
              />
            </div>
          </div>
          
          {/* Store Credit - Inline when available */}
          {shouldShowStoreCredit && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-dashed border-slate-200">
              <Wallet className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <Label className="text-xs text-emerald-700 shrink-0">
                Credit (₹{availableStoreCredit.toFixed(0)} avail)
              </Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                max={Math.min(availableStoreCredit, total)}
                value={storeCreditAmount || ""}
                onChange={(e) => {
                  const val = parseFloat(e.target.value || "0");
                  setStoreCreditAmount(Math.min(val, availableStoreCredit, total));
                }}
                className="font-mono h-8 text-sm px-2 text-right flex-1 border-emerald-200"
                data-testid="payment-store-credit"
              />
            </div>
          )}
          
          {/* Outstanding Dues - Compact inline */}
          {customer && outstandingDues > 0 && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-dashed border-slate-200">
              <AlertCircle className="w-3.5 h-3.5 text-orange-500 shrink-0" />
              <Label className="text-xs text-orange-600 shrink-0">
                Dues (₹{outstandingDues.toFixed(0)})
              </Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                max={outstandingDues}
                value={duesCollectionAmount || ""}
                onChange={(e) => {
                  const val = parseFloat(e.target.value || "0");
                  setDuesCollectionAmount(Math.min(val, outstandingDues));
                }}
                className="font-mono h-8 text-sm px-2 text-right flex-1 border-orange-200"
                data-testid="payment-collect-dues"
              />
              <select
                value={duesCollectionMode}
                onChange={(e) => setDuesCollectionMode(e.target.value)}
                className="h-8 text-xs px-1 rounded border border-orange-200 bg-white"
                data-testid="payment-dues-mode"
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
              </select>
            </div>
          )}
        </div>
        
        {/* Cash Received & Change - Compact */}
        {cashAmount > 0 && (
          <div className={`py-2 border-b -mx-4 px-4 ${cashShortfall > 0.01 ? 'bg-red-50/50 border-red-100' : 'bg-blue-50/50 border-slate-100'}`}>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 flex-1">
                <Label className={`text-xs shrink-0 ${cashShortfall > 0.01 ? 'text-red-700' : 'text-blue-700'}`}>Cash Received</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={cashReceived || ""}
                  onChange={(e) => setCashReceived(parseFloat(e.target.value || "0"))}
                  className={`font-mono h-8 text-sm px-2 text-right flex-1 ${cashShortfall > 0.01 ? 'border-red-300 bg-red-50' : 'border-blue-200'}`}
                  data-testid="payment-cash-received"
                />
              </div>
              {cashChange > 0 && cashShortfall <= 0.01 && (
                <div className="text-right shrink-0">
                  <div className="text-[10px] text-blue-600">Change</div>
                  <div className="font-mono text-lg font-bold text-blue-800" data-testid="payment-change">
                    ₹{cashChange.toFixed(2)}
                  </div>
                </div>
              )}
            </div>
            {/* Cash Shortfall Warning */}
            {cashShortfall > 0.01 && (
              <div className="mt-1.5 text-xs text-red-600 font-medium flex items-center gap-1" data-testid="payment-cash-shortfall">
                <AlertCircle className="w-3.5 h-3.5" />
                Cash short by ₹{cashShortfall.toFixed(2)}
              </div>
            )}
          </div>
        )}
        
        {/* Payment Status - Compact Summary */}
        <div className="py-2 flex items-center justify-between text-xs">
          <div className="space-y-0.5">
            <div className="text-slate-500">Paid: <span className="font-mono font-medium text-green-600" data-testid="payment-paid-amount">₹{paidAmount.toFixed(2)}</span></div>
            {shortfall > 0.01 && (
              <div className="text-red-600 font-medium">Short: ₹{shortfall.toFixed(2)}</div>
            )}
            {creditAmount > 0 && (
              <div className="text-amber-600" data-testid="payment-on-credit">On Credit: ₹{creditAmount.toFixed(2)}</div>
            )}
          </div>
          {duesCollectionAmount > 0 && (
            <div className="text-orange-600 text-right">
              <div className="text-[10px]">+Dues</div>
              <div className="font-mono">₹{duesCollectionAmount.toFixed(2)}</div>
            </div>
          )}
        </div>
        
        {/* Validation Messages - Compact */}
        {shortfall > 0.01 && (
          <div className="text-xs text-red-600 py-1.5 px-2 rounded bg-red-50 border border-red-200 -mx-4 mx-0" data-testid="payment-error-shortfall">
            Short by ₹{shortfall.toFixed(2)}
          </div>
        )}
        
        {excessAmount > 0.01 && customer && (
          <div className="py-1.5 px-2 rounded bg-blue-50 border border-blue-200" data-testid="payment-excess">
            <label className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="text-xs text-blue-700">Excess ₹{excessAmount.toFixed(2)} → store as credit?</span>
              <input
                type="checkbox"
                checked={storeExcessAsCredit}
                onChange={(e) => setStoreExcessAsCredit(e.target.checked)}
                className="rounded border-blue-300 text-blue-600"
                data-testid="payment-store-excess-checkbox"
              />
            </label>
          </div>
        )}
        
        {excessAmount > 0.01 && !customer && (
          <div className="text-xs text-amber-600 py-1.5 px-2 rounded bg-amber-50 border border-amber-200" data-testid="payment-excess-no-customer">
            Excess ₹{excessAmount.toFixed(2)} — select customer to store as credit
          </div>
        )}
        
        {/* Action Buttons - Always visible */}
        <DialogFooter className="pt-3 flex justify-between gap-2 border-t border-slate-100 mt-2">
          <Button
            variant="outline"
            onClick={onBack}
            className="gap-1.5 h-9 text-sm"
            data-testid="payment-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sale
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || !canComplete}
            className="gap-1.5 h-9 text-sm text-white hover:opacity-90"
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="payment-confirm-btn"
          >
            {saving ? "Processing..." : (
              <>
                <Check className="w-4 h-4" />
                Confirm & Complete
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
